import { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import Pagination, { PaginationNew } from "@/helper/Pagination";
import { ButtonShadcn } from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import { cn } from "@/lib/utils";

/**
 * Tracks whether a wide table still has columns hidden off its right edge, and
 * drives the fade affordance that backs up the thin `custom-scrollbar` — the
 * reported symptom was the last columns simply appearing "cut off", and a 4px
 * bar alone is easy to miss on a scaled display.
 *
 * This used to ALSO map a vertical wheel onto `scrollLeft`, so hidden columns
 * could be reached without finding the scrollbar. That was removed, and the
 * reason is worth keeping: the mapping was harmless only while no table
 * actually overflowed. Once one did, every downward wheel over the table went
 * sideways instead of down, and a 50-row page could not be scrolled at all. It
 * was reported twice.
 *
 * Intercepting a plain vertical wheel is a bet that the user wants to move
 * across rather than down, and on a long table that bet is wrong far more often
 * than it is right — while being wrong costs the reader the whole page.
 * Horizontal reach is left to the three mechanisms the browser already
 * provides and that nothing here can break: the scrollbar, shift+wheel, and
 * trackpad horizontal gestures.
 */
const useHorizontalWheelScroll = () => {
  const ref = useRef(null);
  // Whether columns are currently hidden off the right edge. Drives the fade
  // affordance below, which backs up the 4px scrollbar — the reported symptom
  // was the last columns simply appearing "cut off", and a thin bar alone is
  // easy to miss on a scaled display.
  const [hasMoreRight, setHasMoreRight] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const sync = () => setHasMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);

    sync();
    el.addEventListener("scroll", sync, { passive: true });

    // The wrapper resizes when the window/sidebar does; the table resizes when
    // a page of rows loads. Both change whether anything is hidden, and only
    // the second one fires without a window resize.
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);

    return () => {
      el.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, []);

  return { ref, hasMoreRight };
};

// `fitContent` opts a table out of being squeezed into its container: columns
// keep their natural width and the wrapper scrolls instead of cells getting
// crushed. Off by default so narrow tables still stretch to fill the page.
const tableClass = (fitContent) =>
  // `whitespace-nowrap` is what actually makes `fitContent` work. `min-w-max`
  // sizes the table to its content, but if every cell is free to wrap, the
  // content collapses to fit whatever width is available and the table never
  // overflows -- so there is nothing for the `overflow-x-auto` container to
  // scroll, and the right-hand columns stay unreachable however wide the data
  // is. Columns that genuinely want to wrap opt back in via `col.wrap`, which
  // applies `whitespace-normal` on the cell and wins over this.
  `text-xs border-separate border-spacing-0 ${fitContent ? "w-full min-w-max whitespace-nowrap" : "w-full"}`;
// `rowKey` is a column name, or a function for a list whose rows do not share
// one id-shaped key — a dossier section, for instance, where `identity` rows
// carry `candidateId` and `permissions` rows carry `code`.
const keyOf = (row, index, rowKey) => {
  const value = typeof rowKey === "function" ? rowKey(row, index) : row?.[rowKey];
  return value == null || value === "" ? `row-${index}` : String(value);
};

const DataTable = ({
  className = "",
  columns = [],
  rowKey = "id",
  api,
  apiData = [],
  search = "",
  sortable = true,
  defaultSort = { key: "applied_at", order: "desc" },
  setTotal,
  setLoading,
  extraParams = {},
  onRowClick,
  onDataLoaded,
  currentPage: externalPage,
  pageSize: externalPageSize,
  onPageChange,
  onPageSizeChange,
  showPagination = true,
  paginationLabel = "items",
  fitContent = false,
}) => {
  const debouncedSearch = useDebounce(search, 500);
  const extraParamsString = JSON.stringify(extraParams);
  const apiDataString = JSON.stringify(apiData);
  const { ref: scrollRef, hasMoreRight } = useHorizontalWheelScroll();

  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [sortKey, setSortKey] = useState(defaultSort.key);
  const [sortDir, setSortDir] = useState(defaultSort.order);
  const [total, setTotalState] = useState(0);
  // See the note on `failed` in DataTableNew — a rejected request is not an
  // empty tenant, and the two must not read the same. `reloadKey` is what
  // "Try again" bumps: the fetch lives inside the effect here.
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Guards against out-of-order responses, same as DataTableNew's fetchData:
  // a stale response landing after a newer one must not overwrite it.
  const requestSeq = useRef(0);

  const currentPage = externalPage !== undefined ? externalPage : 1;
  const pageSize = externalPageSize !== undefined ? externalPageSize : 10;

  useEffect(() => {
    if (apiData && apiData.length > 0) {
      const sorted = sortable
        ? [...apiData].sort((a, b) => {
            const aVal = a?.[sortKey];
            const bVal = b?.[sortKey];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            const cmp = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;

            return sortDir === "asc" ? cmp : -cmp;
          })
        : [...apiData];

      const start = (currentPage - 1) * pageSize;
      const paginatedData = sorted.slice(start, start + pageSize);

      setData(paginatedData);
      setTableLoading(false);
      setLoading?.(false);
      onDataLoaded?.(sorted);
      setTotal?.(sorted.length);
      setTotalState(sorted.length);
      return;
    }

    if (!api) {
      setData([]);
      setTableLoading(false);
      setLoading?.(false);
      onDataLoaded?.([]);
      return;
    }

    const fetchData = async () => {
      const seq = ++requestSeq.current;

      try {
        setTableLoading(true);
        setLoading?.(true);
        setFailed(false);

        const res = await api({
          search: debouncedSearch,
          sort_by: sortKey,
          sort_order: sortDir,
          page: currentPage,
          per_page: pageSize,
          ...extraParams,
        });

        if (seq !== requestSeq.current) return;

        const list = res?.data?.candidates ?? res?.candidates ?? res?.data ?? [];
        const pg = res?.data?.pagination ?? {};
        const totalItems = pg.total ?? list.length ?? 0;

        setData(Array.isArray(list) ? list : []);
        setTotalState(totalItems);
        setTotal?.(totalItems);
        onDataLoaded?.(Array.isArray(list) ? list : []);
      } catch (e) {
        if (seq !== requestSeq.current) return;
        console.error("Table fetch error", e);
        setData([]);
        setTotalState(0);
        setFailed(true);
        onDataLoaded?.([]);
      } finally {
        if (seq === requestSeq.current) {
          setTableLoading(false);
          setLoading?.(false);
        }
      }
    };

    fetchData();
  }, [api, debouncedSearch, sortKey, sortDir, extraParamsString, apiDataString, currentPage, pageSize, reloadKey]);

  const handleSort = (key) => {
    if (!sortable) return;
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    if (onPageChange) onPageChange(1);
  };

  const handlePageChange = (page) => {
    if (onPageChange) onPageChange(page);
  };

  const handlePageSizeChange = (size) => {
    if (onPageSizeChange) onPageSizeChange(size);
    if (onPageChange) onPageChange(1);
  };

  const SortIcon = ({ col }) => {
    if (!col.sortable || !sortable) return null;
    const activeSortKey = col.sortKey || col.key;
    if (sortKey !== activeSortKey) {
      return <ChevronsUpDown size={11} className="text-text-primary opacity-50" />;
    }
    return sortDir === "asc" ? <ChevronUp size={11} className="text-text-primary" /> : <ChevronDown size={11} className="text-text-primary" />;
  };

  const getCellWrapClass = (col) => {
    return col.wrap ||
      col.key?.toLowerCase().includes("date") ||
      col.key?.toLowerCase().includes("applied") ||
      (typeof col.label === "string" && col.label?.toLowerCase().includes("date"))
      ? "whitespace-normal min-w-[100px] break-words"
      : "";
  };

  const SkeletonRow = ({ isLast = false }) => (
    <tr>
      {columns.map((_, i) => (
        <td
          key={i}
          className={[
            "px-3 py-3 bg-table-bg",
            !isLast ? "border-b border-border-primary" : "",
            isLast && i === 0 ? "rounded-bl-xl" : "",
            isLast && i === columns.length - 1 ? "rounded-br-xl" : "",
          ].join(" ")}
        >
          <div className="h-3.5 w-3/4 rounded bg-background-secondary/60 animate-pulse" />
        </td>
      ))}
    </tr>
  );

  return (
    <div className={`w-full min-w-0 ${className}`}>
      <div className="relative">
        <div ref={scrollRef} className="overflow-x-auto custom-scrollbar">
          <table className={tableClass(fitContent)}>
            <thead>
              <tr>
                {columns.map((col, index) => {
                  const activeSortKey = col.sortKey || col.key;

                  return (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && sortable && handleSort(activeSortKey)}
                      className={[
                        "px-3 py-2.5 text-left text-xs font-semibold tracking-[0.02em]",
                        "text-text-primary bg-background-form border-b border-border-primary",
                        getCellWrapClass(col).replace("break-words", ""),
                        col.sortable && sortable ? "cursor-pointer select-none" : "",
                        col.align === "center" ? "text-center" : "",
                        col.align === "right" ? "text-right" : "",
                      ].join(" ")}
                    >
                      <span className="inline-flex items-center gap-1">
                        {typeof col.label === "string" ? <span>{col.label}</span> : col.label}
                        <SortIcon col={col} />
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {tableLoading ? (
                Array.from({ length: pageSize }).map((_, i, arr) => <SkeletonRow key={i} isLast={i === arr.length - 1} />)
              ) : failed ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-6 text-center text-xs text-text-muted bg-background-secondary">
                    <span className="text-text-primary">This table could not be loaded.</span>{" "}
                    <ButtonShadcn variant="link" size="xs" onClick={() => setReloadKey((k) => k + 1)} className="h-auto p-0 text-accent-text">
                      Try again
                    </ButtonShadcn>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-6 text-center text-xs text-text-muted bg-background-secondary">
                    No data available
                  </td>
                </tr>
              ) : (
                data.map((row, ri) => {
                  const isLastRow = ri === data.length - 1;

                  return (
                    <tr key={keyOf(row, ri, rowKey)} onClick={() => onRowClick?.(row)} className={onRowClick ? "cursor-pointer" : ""}>
                      {columns.map((col, ci) => (
                        <td
                          key={col.key}
                          className={[
                            "px-3 py-2.5 text-xs text-text-primary bg-background-secondary transition-colors",
                            !isLastRow ? "border-b border-border-primary" : "border-b-0",
                            onRowClick ? "hover:bg-background-primary" : "",
                            getCellWrapClass(col),
                            col.align === "center" ? "text-center" : "",
                            col.align === "right" ? "text-right" : "",
                            isLastRow && ci === 0 ? "rounded-bl-xl" : "",
                            isLastRow && ci === columns.length - 1 ? "rounded-br-xl" : "",
                            col.className || "",
                          ].join(" ")}
                        >
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Only for tables that opted into content sizing, and only while
            something is actually hidden — a table that fits looks unchanged. */}
        {fitContent && hasMoreRight && (
          <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background-secondary to-transparent" />
        )}
      </div>

      {showPagination && total > 0 && (
        <div className="flex items-center justify-between bg-background-secondary rounded-b-xl px-3 py-2.5 border-t border-border-primary">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span>Items per page</span>
            <div className="w-[72px]">
              <Select
                value={pageSize}
                onChange={(value) => handlePageSizeChange(Number(value))}
                options={[5, 10, 25, 50].map((n) => ({ value: n, label: String(n) }))}
                className="h-7 px-2 text-xs font-normal"
              />
            </div>
          </div>

          <Pagination currentPage={currentPage} totalItems={total} pageSize={pageSize} onPageChange={handlePageChange} label={paginationLabel} />
        </div>
      )}
    </div>
  );
};

export const DataTableNew = forwardRef(({
  className = "",
  columns = [],
  rowKey = "id",
  api,
  search = "",
  apiData = [],
  sortable = true,
  defaultSort = { key: "created_at", order: "desc" },
  onDataFetch,
  extraParams = {},
  onRowClick,
  showPagination = true,
  showPaginationLabel = true,
  paginationLabel = "items",
  dataPath = "data",
  itemSize = 10,
  silentFetch = false,
  // Bump to force a refetch without changing query params. Used by callers that
  // learn about server-side changes out of band (e.g. a WebSocket event) and
  // need the current page re-read. Optional: existing callers omit it and the
  // fetch effect behaves exactly as before.
  reloadKey = 0,
  fitContent = false,
  // Seed the paging state from outside — a screen that keeps its page in the
  // URL restores it on mount. The table owns the state from then on, and
  // reports every change back through the two callbacks, so the URL can
  // follow without a second source of truth fighting `currentPage`.
  initialPage = 1,
  onPageChange,
  onPageSizeChange,
  // Matrix-style extras (role x permission grids, etc). All default off so
  // every existing flat-list consumer renders exactly as before.
  stickyHeader = false,
  stickyFirstColumn = false,
  maxHeight,
  highlightRowCol = false,
  // What an empty table says. A page whose empty state names its own thing
  // ("No messages yet") passes it; everything else keeps the default.
  emptyMessage = "No data available",
}, ref) => {
  // Guards against out-of-order responses: two fetchData() calls can overlap
  // (a toggle's own refetch racing a websocket-triggered one), and without
  // this a stale response landing after a newer one would silently overwrite
  // it. Only the response to the most-recently-issued request is applied.
  const requestSeq = useRef(0);
  const debouncedSearch = useDebounce(search, 500);
  const extraParamsString = JSON.stringify(extraParams);
  const { ref: scrollRef, hasMoreRight } = useHorizontalWheelScroll();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortKey, setSortKey] = useState(defaultSort.key);
  const [sortDir, setSortDir] = useState(defaultSort.order);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(itemSize);
  const [pagination, setPagination] = useState({ hasNext: false, hasPrev: false });
  const [extraData, setExtraData] = useState({});
  // A failed fetch used to fall through to "No data available", so a rejected
  // sort or a dropped connection read as "this tenant has nothing". The two
  // are different answers and the reader has a different next move for each.
  const [failed, setFailed] = useState(false);
  // The api interceptor words a refusal for the reader (a 403, a module that
  // is off) as `userMessage`; the table shows that rather than a sentence
  // that says only "could not be loaded".
  const [failedMessage, setFailedMessage] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredCol, setHoveredCol] = useState(null);

  const getData = (obj, path) => {
    if (typeof path === "function") return path(obj);
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const fetchData = async () => {
    if (!api) {
      setData([]);
      setIsLoading(false);
      return;
    }

    const seq = ++requestSeq.current;

    try {
      !silentFetch && setIsLoading(true);
      setFailed(false);

      const res = await api({
        search: debouncedSearch,
        sort_by: sortKey,
        sort_order: sortDir,
        page: currentPage,
        per_page: pageSize,
        pagination: showPagination,
        ...extraParams,
      });

      // A newer fetchData() started after this one — a fresher response is
      // already applied, or on its way. Applying this one now would revert it.
      if (seq !== requestSeq.current) return;

      const list = getData(res.data, dataPath) ?? [];
      const pg = res?.data?.pagination ?? {};
      const totalItems = pg.total ?? list.length ?? 0;
      const resolvedPage = pg.page ?? currentPage;
      const resolvedPageSize = pg.per_page ?? pg.perPage ?? pageSize;
      const resolvedTotalPages = pg.total_pages ?? pg.totalPages ?? Math.ceil(totalItems / resolvedPageSize);

      setData(Array.isArray(list) ? list : []);
      setTotal(totalItems);
      setExtraData(res?.data && typeof res.data === "object" ? res.data : {});
      setPagination({
        hasNext: pg.has_next ?? pg.hasNext ?? resolvedPage < resolvedTotalPages,
        hasPrev: pg.has_prev ?? pg.hasPrev ?? resolvedPage > 1,
      });
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("Table fetch error:", error);
      setData([]);
      setTotal(0);
      setExtraData({});
      setFailed(true);
      setFailedMessage(error?.userMessage || null);
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  };

  // Lets a caller flip a row's fields immediately (optimistic update) without
  // waiting for a refetch — e.g. a status toggle whose own visual feedback
  // must not depend on refetch/websocket timing.
  useImperativeHandle(ref, () => ({
    patchRow: (id, patch) => {
      setData((prev) => prev.map((row) => (String(row?.[rowKey]) === String(id) ? { ...row, ...patch } : row)));
    },
  }));

  // A new search or filter answers about a different set of rows, so page 1
  // is the only page that means anything. Skipped on mount, so `initialPage`
  // still restores the page a URL named.
  const filtersSeen = useRef(false);
  useEffect(() => {
    if (!filtersSeen.current) {
      filtersSeen.current = true;
      return;
    }
    setCurrentPage(1);
    onPageChange?.(1);
  }, [debouncedSearch, extraParamsString]);

  useEffect(() => {
    fetchData();
  }, [api, debouncedSearch, sortKey, sortDir, extraParamsString, currentPage, pageSize, reloadKey]);

  // Refetches when a caller switches silentFetch on. Not on mount: the fetch
  // effect above already asked, and a second request here raced it.
  const silentSeen = useRef(false);
  useEffect(() => {
    if (!silentSeen.current) {
      silentSeen.current = true;
      return;
    }
    if (silentFetch) fetchData();
  }, [silentFetch]);

  useEffect(() => {
    apiData.length > 0 && setData(apiData);
  }, [apiData]);

  useEffect(() => {
    onDataFetch && onDataFetch({ ...extraData, total, isLoading });
  }, [total, isLoading, extraData]);

  const handleSort = (key) => {
    if (!sortable) return;

    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }

    setCurrentPage(1);
    onPageChange?.(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    onPageChange?.(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    onPageSizeChange?.(size);
    onPageChange?.(1);
  };

  const SortIcon = ({ col }) => {
    if (!col.sortable || !sortable) return null;
    const activeSortKey = col.sortKey || col.key;

    if (sortKey !== activeSortKey) {
      return <ChevronsUpDown size={11} className="text-text-primary opacity-50" />;
    }

    return sortDir === "asc" ? <ChevronUp size={11} className="text-text-primary" /> : <ChevronDown size={11} className="text-text-primary" />;
  };

  const getCellWrapClass = (col) => {
    return col.wrap ||
      col.key?.toLowerCase().includes("date") ||
      col.key?.toLowerCase().includes("applied") ||
      (typeof col.label === "string" && col.label?.toLowerCase().includes("date"))
      ? "whitespace-normal min-w-[100px] break-words"
      : "";
  };

  const SkeletonRow = ({ isLast = false }) => (
    <tr>
      {columns.map((_, i) => (
        <td
          key={i}
          className={[
            "px-3 py-3 bg-table-bg",
            !isLast ? "border-b border-border-primary" : "",
            isLast && i === 0 ? "rounded-bl-xl" : "",
            isLast && i === columns.length - 1 ? "rounded-br-xl" : "",
          ].join(" ")}
        >
          <div className="h-3.5 w-3/4 rounded bg-background-secondary/60 animate-pulse" />
        </td>
      ))}
    </tr>
  );

  const isFrozen = (index) => stickyFirstColumn && index === 0;

  return (
    <div className={`w-full ${className}`}>
      <div
        className={cn("overflow-x-auto custom-scrollbar", stickyHeader ? "overflow-y-auto custom-scrollbar" : "")}
        style={stickyHeader && maxHeight ? { maxHeight } : undefined}
        onMouseLeave={
          highlightRowCol
            ? () => {
                setHoveredRow(null);
                setHoveredCol(null);
              }
            : undefined
        }
      >
        <table
          className={cn(
            "text-xs border-separate border-spacing-0",
            // A wide, dynamic-column matrix (stickyFirstColumn/stickyHeader)
            // needs its natural content width so columns keep their own size
            // and the wrapper scrolls horizontally — `w-full` would instead
            // squeeze every column down to fit the container, which is what
            // a handful of fixed flat-list columns wants.
            stickyFirstColumn || stickyHeader ? "w-max min-w-full" : "w-full",
          )}
        >
          <thead>
            <tr>
              {columns.map((col, index) => {
                const activeSortKey = col.sortKey || col.key;

                return (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && sortable && handleSort(activeSortKey)}
                    onMouseEnter={highlightRowCol ? () => setHoveredCol(index) : undefined}
                    title={col.title}
                    className={cn(
                      "px-3 py-2.5 text-left text-xs font-semibold tracking-[0.02em]",
                      "text-text-primary bg-background-form border-b border-border-primary",
                      getCellWrapClass(col).replace("break-words", ""),
                      col.sortable && sortable ? "cursor-pointer select-none" : "",
                      col.align === "center" ? "text-center" : "",
                      col.align === "right" ? "text-right" : "",
                      index === 0 ? "rounded-tl-xl" : "",
                      index === columns.length - 1 ? "rounded-tr-xl" : "",
                      stickyHeader ? "sticky top-0" : "",
                      isFrozen(index) ? "sticky left-0 border-r" : "",
                      stickyHeader && isFrozen(index) ? "z-30" : stickyHeader ? "z-20" : isFrozen(index) ? "z-10" : "",
                      highlightRowCol && hoveredCol === index ? "bg-background-tertiary/70" : "",
                    )}
                    style={col?.style ?? {}}
                  >
                    <span className="inline-flex items-center gap-1">
                      {typeof col.label === "string" ? <span>{col.label}</span> : col.label}
                      <SortIcon col={col} />
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              Array.from({ length: pageSize }).map((_, i, arr) => <SkeletonRow key={i} isLast={i === arr.length - 1} />)
            ) : failed ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-text-muted bg-table-bg rounded-b-xl">
                  <span className="text-text-primary">{failedMessage || "This table could not be loaded."}</span>{" "}
                  <ButtonShadcn variant="link" size="xs" onClick={fetchData} className="h-auto p-0 text-sm text-accent-text">
                    Try again
                  </ButtonShadcn>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-text-muted bg-table-bg rounded-b-xl">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, ri) => {
                const isLastRow = ri === data.length - 1;
                return (
                  <tr key={keyOf(row, ri, rowKey)} onClick={() => onRowClick?.(row)} className={onRowClick ? "cursor-pointer" : ""}>
                    {columns.map((col, ci) => (
                      <td
                        key={col.key + ci}
                        onMouseEnter={
                          highlightRowCol
                            ? () => {
                                setHoveredRow(ri);
                                setHoveredCol(ci);
                              }
                            : undefined
                        }
                        className={cn(
                          "px-3 py-2.5 text-xs text-text-primary bg-background-secondary transition-colors",
                          !isLastRow ? "border-b border-border-primary" : "border-b-0",
                          onRowClick ? "hover:bg-table-heading" : "",
                          getCellWrapClass(col),
                          col.align === "center" ? "text-center" : "",
                          col.align === "right" ? "text-right" : "",
                          isLastRow && ci === 0 ? "rounded-bl-xl" : "",
                          isLastRow && ci === columns.length - 1 ? "rounded-br-xl" : "",
                          col.className || "",
                          isFrozen(ci) ? "sticky left-0 z-10 border-r border-border-primary" : "",
                          highlightRowCol && (hoveredRow === ri || hoveredCol === ci) ? "bg-background-tertiary/40" : "",
                        )}
                      >
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPaginationLabel && total > 0 && (
        <PaginationNew
          currentPage={currentPage}
          totalItems={total}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          label={paginationLabel}
          hasNext={pagination.hasNext}
          hasPrev={pagination.hasPrev}
        />
      )}
    </div>
  );
});

DataTableNew.displayName = "DataTableNew";

export default DataTable;
